import type { Configuration, PopupRequest } from '@azure/msal-browser';

// Defaults are the live dev values; can be overridden at build time via VITE_* env.
const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID ?? '0b814ca9-2136-40c7-a8e2-3120fcc316a2';
const TENANT_ID = import.meta.env.VITE_AAD_TENANT_ID ?? '1c3bd754-c584-4d37-bb70-15b434094c82';

export const API_SCOPE = `api://${CLIENT_ID}/access_as_user`;

/**
 * Redirect target for MSAL's hidden silent-renew iframe.
 *
 * MUST NOT be the app itself. MSAL renews tokens by navigating a hidden iframe
 * to `/authorize?prompt=none` and reading the response off the resulting URL.
 * When that URL serves the SPA, a second copy of the app boots inside the
 * frame, requests its own token, and opens another iframe — a recursion that
 * produced ~20 `authorize` requests in 10s, never completed silent renewal, and
 * forced an interactive sign-in on every visit.
 *
 * `blank.html` is a dedicated page that loads ONLY the MSAL redirect bridge, so
 * the frame posts the auth response back to the parent and stops. It must never
 * load the SPA. Every origin listed here must also be registered as an SPA
 * redirect URI on the app registration.
 *
 * NOTE (msal-browser v5): the bridge page is no longer script-FREE. v4 had the
 * parent poll the iframe's URL, so a blank page sufficed. v5 waits on a
 * `BroadcastChannel` that the redirect page must post to, so a script-free page
 * stalls silent renewal for the full `iframeBridgeTimeout` and then degrades to
 * an interactive sign-in. See src/auth-redirect-bridge.ts and
 * docs/INCIDENTS.md → INC-003 (v5 addendum).
 */
export const SILENT_REDIRECT_URI =
  typeof window !== 'undefined' ? `${window.location.origin}/blank.html` : '/blank.html';

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}/v2.0`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin + '/' : '/'
  },
  cache: {
    // localStorage so the MSAL account + cached tokens survive tab close /
    // new-tab navigation. Without this, opening the SPA in a fresh tab starts
    // with no account, `tokenProvider()` returns null, and the first
    // /api/mgmt/* call goes out unauthenticated → 401 (which then "fixes
    // itself" once RequireAuth triggers the silent SSO redirect).
    cacheLocation: 'localStorage'
  },
  system: {
    /**
     * How long to wait for `blank.html` to post the silent-auth response back
     * over its `BroadcastChannel` before giving up and falling back to an
     * interactive redirect. MSAL's default is 10,000 ms.
     *
     * This is a BACKSTOP, not the fix — a working bridge answers in well under
     * a second (the Entra `authorize` round-trip itself measured ~400 ms). We
     * lower it so that if the bridge ever regresses again, users lose 3s
     * instead of 10s before the interactive fallback kicks in. Don't go much
     * below this: a cold DNS/TLS path to login.microsoftonline.com can be
     * slower than the warm case, and a premature timeout means an unnecessary
     * full sign-in redirect.
     */
    iframeBridgeTimeout: 3000
  }
};

export const loginRequest: PopupRequest = {
  scopes: [API_SCOPE, 'openid', 'profile']
};

/**
 * Use for ANY interactive sign-in (user-initiated SignInButton, or fallback
 * after a silent flow fails). Intentionally has NO `prompt` set:
 *
 *   - A separate top-level `prompt: 'none'` request re-entered an already
 *     latched AADSTS50196 flow before the following ordinary request
 *     succeeded. Do not restore that extra cycle.
 *   - With no prompt, Entra uses the sole active session if valid, shows an
 *     account picker for multiple sessions, or falls through to its normal
 *     credential page. This preserves first-party SSO without a preceding
 *     forced-silent request.
 */
export const interactiveLoginRequest: PopupRequest = {
  scopes: [API_SCOPE, 'openid', 'profile']
};
